import { inject, injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { IAnalyticsGatewayToken, type IAnalyticsGateway } from '@/shared/analytics/AnalyticsGateway';
import { findTopicInSurface } from './AnalyticsSurface';
import { SurfaceRegistry } from './SurfaceRegistry';
import type { TopicDefinition } from './TopicCatalogTypes';
import { buildDriftUserRows, driftCostByUser, driftTokensByUser, type DriftUserRow, type DriftUserTotals } from './DriftMetrics';
import type { AdHocAggregateResult, AdHocAggregateRow, AnalyticsFilter, AnalyticsMetric } from '@/shared/analytics/AnalyticsTypes';

/** Records fetched for the drifted sessions; generous enough to cover an anomaly-scale population. */
const DRIFT_RECORD_LIMIT = 2000;
/**
 * The telemetry analytics API caps each `filters[].values` array at 500
 * (`AnalyticsQueryValidation`), while a drift run can surface up to 1000 drifted
 * sessions — so the `session_id` IN-list is chunked to stay within the cap.
 */
const MAX_FILTER_VALUES = 500;

/** Splits ids into chunks no larger than `size` so each fits the per-filter value cap. */
function chunkIds(ids: string[], size: number): string[][] {
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += size) chunks.push(ids.slice(i, i + size));
    return chunks;
}
const TOTALS_METRICS: AnalyticsMetric[] = ['totalTokens', 'sessions'];
const ZERO_METRICS: Record<AnalyticsMetric, number> = { turns: 0, sessions: 0, users: 0, inputTokens: 0, outputTokens: 0, totalTokens: 0, avgOutputTokens: 0, avgConfidence: 0, avgModelFit: 0, allocatedCostUsd: 0 };

/**
 * Shapes the per-person drift rows into the aggregate the insights panel narrates:
 * one row per person keyed by user and ranked heaviest-first, carrying their drift
 * tokens in the `totalTokens` metric slot so the callouts read as "who drifts most".
 */
function driftInsightsResult(rows: DriftUserRow[]): AdHocAggregateResult | null {
    if (rows.length === 0) return null;
    const ranked = [...rows].sort((a, b) => b.driftTokens - a.driftTokens);
    const aggRows: AdHocAggregateRow[] = ranked.map((row) => ({ keys: { user_id: row.userId }, metrics: { ...ZERO_METRICS, totalTokens: row.driftTokens } }));
    const totalDrift = ranked.reduce((sum, row) => sum + row.driftTokens, 0);
    return { rows: aggRows, totals: { ...ZERO_METRICS, totalTokens: totalDrift }, distinctCount: ranked.length };
}

/**
 * L1 view model for the people-first Session Drift overview. Builds one row per
 * person who has ≥1 drifted session, where all totals span the person's whole
 * activity and only the drift tokens come from their drifted sessions.
 */
@injectable()
export class SessionDriftOverviewViewModel extends BaseViewModel {
    definition: TopicDefinition | null = null;
    rows: DriftUserRow[] = [];
    /** Per-person drift aggregate for the side-rail insights panel; null until rows load. */
    insights: AdHocAggregateResult | null = null;
    driftedSessionCount = 0;
    notFound = false;
    loading = false;
    errorMessage: string | null = null;

    constructor(
        @inject(IAnalyticsGatewayToken) private gateway: IAnalyticsGateway,
        @inject(SurfaceRegistry) private registry: SurfaceRegistry,
    ) {
        super();
    }

    async load(surfaceId: string, topicId: string | undefined, ambientFilters: AnalyticsFilter[] = []): Promise<void> {
        const surface = this.registry.find(surfaceId);
        const definition = surface === undefined ? undefined : findTopicInSurface(surface, topicId);
        if (definition === undefined) {
            this.notFound = true;
            this.notifyChange();
            return;
        }

        this.notFound = false;
        this.definition = definition;
        this.rows = [];
        this.insights = null;
        this.driftedSessionCount = 0;
        this.loading = true;
        this.errorMessage = null;
        this.notifyChange();

        try {
            const anomalies = await this.gateway.anomalies({ kind: 'session_drift', filters: ambientFilters, limit: 1000 });
            const driftedSessionIds = [
                ...new Set(anomalies.events.map((event) => event.drillKey).filter((key): key is string => key !== undefined)),
            ];
            this.driftedSessionCount = driftedSessionIds.length;

            if (driftedSessionIds.length === 0) {
                this.rows = [];
                return;
            }

            const sessionIdChunks = chunkIds(driftedSessionIds, MAX_FILTER_VALUES);
            const [driftRecordPages, totals, topics] = await Promise.all([
                Promise.all(
                    sessionIdChunks.map((sessionIds) =>
                        this.gateway.records({
                            source: 'interactions',
                            filters: [...ambientFilters, { field: 'session_id', values: sessionIds }],
                            limit: DRIFT_RECORD_LIMIT,
                        }),
                    ),
                ),
                this.gateway.aggregate({ source: 'interactions', groupBy: ['user_id'], metrics: TOTALS_METRICS, filters: ambientFilters }),
                this.gateway.aggregate({ source: 'interactions', groupBy: ['user_id', 'topic_label'], metrics: ['turns'], filters: ambientFilters }),
            ]);
            const driftRecords = driftRecordPages.flatMap((page) => page.records);

            const userTotals = new Map<string, DriftUserTotals>();
            for (const row of totals.rows) {
                const userId = row.keys.user_id;
                if (userId !== undefined) userTotals.set(userId, { totalTokens: row.metrics.totalTokens, totalSessions: row.metrics.sessions });
            }

            const topicCountByUser = new Map<string, number>();
            for (const row of topics.rows) {
                const userId = row.keys.user_id;
                if (userId !== undefined) topicCountByUser.set(userId, (topicCountByUser.get(userId) ?? 0) + 1);
            }

            this.rows = buildDriftUserRows({
                driftTokensByUser: driftTokensByUser(driftRecords),
                driftCostByUser: driftCostByUser(driftRecords),
                userTotals,
                topicCountByUser,
            });
            this.insights = driftInsightsResult(this.rows);
        } catch (err) {
            this.errorMessage = err instanceof Error ? err.message : String(err);
        } finally {
            this.loading = false;
            this.notifyChange();
        }
    }
}
